import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  UserCheck, 
  DollarSign,
  TrendingUp,
  Bell,
  Search,
  MoreVertical,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import AdminSidebar from '../../components/layout/AdminSidebar';
import { useAuth } from '../../contexts/AppContext';
import { dashboardAPI, appointmentsAPI, patientsAPI, doctorsAPI, realTimeUpdates } from '../../services/api';
import { formatTime, getRelativeDate } from '../../utils';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load dashboard data
  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [appointmentsResult, patientsResult, doctorsResult, statsResult] = await Promise.all([
        appointmentsAPI.getAll(),
        patientsAPI.getAll(),
        doctorsAPI.getAll(),
        dashboardAPI.getStats()
      ]);

      if (appointmentsResult.success && appointmentsResult.data) {
        setAppointments(appointmentsResult.data);
      }

      if (patientsResult.success && patientsResult.data) {
        setPatients(patientsResult.data);
      }

      if (doctorsResult.success && doctorsResult.data) {
        setDoctors(doctorsResult.data);
      }

      if (statsResult.success && statsResult.data) {
        setStats(statsResult.data);
      }

      // Handle any errors
      const errors = [appointmentsResult, patientsResult, doctorsResult, statsResult]
        .filter(result => !result.success)
        .map(result => result.error)
        .join(', ');
      
      if (errors) {
        setError(`Some data failed to load: ${errors}`);
      }
    } catch (err) {
      setError('Network error while loading dashboard data');
      console.error('Error loading dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Redirect if not admin
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
    }
  }, [user, navigate]);

  // Load data on mount
  useEffect(() => {
    if (user && user.role === 'admin') {
      loadDashboardData();
    }
  }, [user]);

  // Set up real-time updates
  useEffect(() => {
    if (user && user.role === 'admin') {
      const handleDashboardUpdate = (updatedStats: any) => {
        setStats(updatedStats);
      };

      realTimeUpdates.subscribe('dashboard', handleDashboardUpdate, 30000);

      return () => {
        realTimeUpdates.unsubscribe('dashboard');
      };
    }
  }, [user]);

  if (!user) return null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const todaysAppointments = appointments.filter(apt => 
    new Date(apt.date).toDateString() === new Date().toDateString()
  );

  const recentPatients = patients.slice(0, 5);

  const statCards = [
    {
      title: 'Today\'s Appointments',
      value: stats.todayAppointments || todaysAppointments.length,
      change: '+12%',
      changeType: 'positive',
      icon: Calendar,
      color: 'bg-blue-500'
    },
    {
      title: 'Total Patients',
      value: stats.totalPatients || patients.length,
      change: '+8%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-green-500'
    },
    {
      title: 'Total Doctors',
      value: doctors.length,
      change: '+2%',
      changeType: 'positive',
      icon: UserCheck,
      color: 'bg-purple-500'
    },
    {
      title: 'Monthly Revenue',
      value: `$${(stats.monthlyRevenue || 0).toLocaleString()}`,
      change: '+15%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'bg-orange-500'
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'scheduled':
        return <Clock className="w-4 h-4 text-blue-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AdminSidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-20'}`}>
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-xl shadow-lg border-b border-slate-200/50 sticky top-0 z-40">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  Dashboard Overview
                </h1>
                <p className="text-slate-600 mt-1 text-lg">Welcome back, <span className="font-semibold text-teal-600">{user.firstName}</span>!</p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    placeholder="Search dashboard..."
                    className="pl-12 pr-4 py-3 w-80 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                <button 
                  onClick={loadDashboardData}
                  disabled={isLoading}
                  className="p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors duration-200 border border-slate-200 flex items-center space-x-2 disabled:opacity-50"
                  title="Refresh Data"
                >
                  <RefreshCw className={`w-5 h-5 text-slate-600 ${isLoading ? 'animate-spin' : ''}`} />
                  <span className="text-sm font-medium text-slate-600">Refresh</span>
                </button>
                <div className="relative">
                  <button className="p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors duration-200 border border-slate-200">
                    <Bell className="w-6 h-6 text-slate-600" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                  </button>
                </div>
                <div className="flex items-center space-x-3 pl-4 border-l border-slate-200">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{user.firstName.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{user.firstName} {user.lastName}</p>
                    <p className="text-xs text-slate-500 capitalize">{user.role} Account</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="p-8">
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {statCards.map((stat, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-200/50">
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-800 mt-2">{stat.value}</p>
                      <div className="flex items-center mt-3">
                        <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          stat.changeType === 'positive' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          <TrendingUp className={`w-3 h-3 mr-1 ${
                            stat.changeType === 'positive' ? 'text-emerald-600' : 'text-red-600 rotate-180'
                      }`} />
                        {stat.change}
                        </div>
                      </div>
                    </div>
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ${stat.color} group-hover:scale-110 transition-transform duration-300`}>
                      <stat.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Today's Appointments */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50">
                <div className="px-6 py-5 border-b border-slate-200/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">
                      Today's Appointments
                    </h2>
                      <p className="text-sm text-slate-500 mt-1">{todaysAppointments.length} scheduled today</p>
                    </div>
                    <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200">
                      <MoreVertical className="w-5 h-5 text-slate-500" />
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="space-y-3">
                    {todaysAppointments.slice(0, 6).map((appointment) => (
                      <div key={appointment.id} className="group p-4 bg-gradient-to-r from-slate-50 to-white rounded-xl border border-slate-100 hover:shadow-md transition-all duration-200">
                        <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-md">
                              <Calendar className="w-6 h-6 text-white" />
                          </div>
                          <div>
                              <p className="font-semibold text-slate-800">
                              Patient #{appointment.patientId}
                            </p>
                              <p className="text-sm text-slate-600">
                                {formatTime(appointment.time)} • Dr. Johnson
                            </p>
                          </div>
                        </div>
                          <div className="flex items-center space-x-3">
                          {getStatusIcon(appointment.status)}
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              appointment.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                              appointment.status === 'cancelled' ? 'bg-red-100 text-red-700' : 
                              'bg-blue-100 text-blue-700'
                            }`}>
                            {appointment.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {todaysAppointments.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Calendar className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="text-slate-600 font-medium">No appointments scheduled for today</p>
                      <p className="text-slate-500 text-sm mt-1">Check back tomorrow or view all appointments</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar Content */}
            <div className="space-y-6">
              {/* Recent Patients */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50">
                <div className="px-6 py-5 border-b border-slate-200/50">
                  <h3 className="text-lg font-bold text-slate-800">
                    Recent Patients
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">Latest registrations</p>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    {recentPatients.map((patient) => (
                      <div key={patient.id} className="flex items-center space-x-4 p-3 hover:bg-slate-50 rounded-xl transition-colors duration-200">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                          <span className="text-white font-bold text-sm">
                            {patient.firstName.charAt(0)}{patient.lastName.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 truncate">
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p className="text-xs text-slate-500">
                            Registered {getRelativeDate(patient.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50">
                <div className="px-6 py-5 border-b border-slate-200/50">
                  <h3 className="text-lg font-bold text-slate-800">
                    Quick Statistics
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">Today's overview</p>
                </div>
                
                <div className="p-6 space-y-5">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium text-slate-700">Upcoming</span>
                    </div>
                    <span className="text-lg font-bold text-blue-600">
                      {stats.upcomingAppointments}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium text-slate-700">Completed</span>
                    </div>
                    <span className="text-lg font-bold text-emerald-600">
                      {stats.completedAppointments}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-rose-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                        <XCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium text-slate-700">Cancelled</span>
                    </div>
                    <span className="text-lg font-bold text-red-600">
                      {stats.cancelledAppointments}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50">
                <div className="px-6 py-5 border-b border-slate-200/50">
                  <h3 className="text-lg font-bold text-slate-800">
                    Quick Actions
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">Common tasks</p>
                </div>
                
                <div className="p-6 space-y-3">
                  <button className="w-full flex items-center justify-start px-4 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all duration-200 shadow-md">
                    <Calendar className="w-5 h-5 mr-3" />
                    <span className="font-semibold">New Appointment</span>
                  </button>
                  <button className="w-full flex items-center justify-start px-4 py-3 border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200">
                    <Users className="w-5 h-5 mr-3" />
                    <span className="font-semibold">Add Patient</span>
                  </button>
                  <button className="w-full flex items-center justify-start px-4 py-3 border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200">
                    <UserCheck className="w-5 h-5 mr-3" />
                    <span className="font-semibold">Add Doctor</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;